import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Upload, Trash2, Copy, Check, LayoutGrid, Grid2x2, Square, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/imageUrl';
import { API_URL } from '@/config/api';
import { useImageUpload } from '@/hooks/useImageUpload';

const VIEW_MODES = [
  { key: 'small',  label: '小',  icon: LayoutGrid,  cols: 'grid-cols-3 md:grid-cols-5 lg:grid-cols-7', imgH: 'h-20' },
  { key: 'medium', label: '中',  icon: Grid2x2,     cols: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4', imgH: 'h-40' },
  { key: 'large',  label: '大',  icon: Square,      cols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', imgH: 'h-64' },
];

const AdminImages = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [viewMode, setViewMode] = useState('medium');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef(null);

  useEffect(() => { fetchImages(); }, []);

  useEffect(() => {
    if (renamingId && renameInputRef.current) renameInputRef.current.focus();
  }, [renamingId]);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API_URL}/images`);
      setImages(response.data);
    } catch {
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const { uploadFiles, uploading, fileInputRef } = useImageUpload(fetchImages);

  const handleFileSelect = (e) => uploadFiles(e.target.files);
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); };
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/images/${deleteTarget}`);
      toast.success('刪除成功');
      fetchImages();
    } catch {
      toast.error('刪除失敗');
    } finally {
      setDeleteTarget(null);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(resolveImageUrl(url));
    setCopiedUrl(url);
    toast.success('網址已複製');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const startRename = (image) => {
    setRenamingId(image.id);
    setRenameValue(image.original_name || '');
  };

  const commitRename = async (image) => {
    if (!renamingId) return;
    setRenamingId(null);
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === image.original_name) return;
    try {
      await axios.patch(`${API_URL}/images/${image.filename}`, { original_name: trimmed });
      toast.success('重命名成功');
      setImages(prev => prev.map(img => img.id === image.id ? { ...img, original_name: trimmed } : img));
    } catch {
      toast.error('重命名失敗');
    }
  };

  const vm = VIEW_MODES.find(v => v.key === viewMode);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold">圖片管理</h1>
            <p className="text-muted-foreground">上傳和管理網站圖片（共 {images.length} 張）</p>
          </div>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex border rounded-lg overflow-hidden">
              {VIEW_MODES.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setViewMode(key)}
                  className={`px-3 py-2 flex items-center gap-1 text-sm transition-colors ${
                    viewMode === key ? 'bg-primary text-white' : 'hover:bg-gray-100'
                  }`}
                  title={`${label}圖示`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? '上傳中...' : '上傳圖片'}
            </Button>
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary hover:bg-gray-50'
          }`}
        >
          <Upload className={`mx-auto h-8 w-8 mb-2 ${dragOver ? 'text-primary' : 'text-gray-400'}`} />
          {uploading ? (
            <p className="text-sm font-medium text-primary">上傳中...</p>
          ) : dragOver ? (
            <p className="text-sm font-medium text-primary">放開以上傳</p>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-600">拖放圖片到這裡</p>
              <p className="text-xs text-muted-foreground mt-1">或點擊選擇檔案 · JPG、PNG、GIF、WebP，最大 5MB</p>
            </>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-12">載入中...</div>
        ) : images.length > 0 ? (
          <div className={`grid gap-3 ${vm.cols}`}>
            {images.map((image) => (
              <div key={image.id} className="group relative bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                {/* Image */}
                <div className={`${vm.imgH} bg-gray-100 overflow-hidden`}>
                  <img
                    src={resolveImageUrl(image.url)}
                    alt={image.original_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Info + actions */}
                <div className="p-2 space-y-1.5">
                  {/* Filename — click to rename */}
                  {renamingId === image.id ? (
                    <Input
                      ref={renameInputRef}
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => commitRename(image)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(image);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      className="h-7 text-xs px-2"
                    />
                  ) : (
                    <div
                      className="flex items-center gap-1 cursor-pointer group/name"
                      onClick={() => startRename(image)}
                      title="點擊重命名"
                    >
                      <p className="text-xs text-muted-foreground truncate flex-1">{image.original_name}</p>
                      <Pencil className="h-3 w-3 text-gray-300 group-hover/name:text-primary flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-7 text-xs"
                      onClick={() => copyToClipboard(image.url)}
                    >
                      {copiedUrl === image.url ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                      複製
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 px-2"
                      onClick={() => setDeleteTarget(image.filename)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            <Upload className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>還沒有上傳任何圖片</p>
            <p className="text-sm mt-1">拖放或點擊上方區域開始上傳</p>
          </div>
        )}
      </div>
      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除這張圖片？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，圖片將永久刪除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminImages;
